import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { supabase } from '../lib/supabase';

interface UserProfile {
  name: string;
  email: string;
  chronological_age: number | null;
  biological_age: number | null;
  gender: string;
  country: string;
  onboarding_completed: boolean;
  weight_kg: number | null;
  height_cm: number | null;
  is_admin?: boolean;
  is_pro_member?: boolean;              // Added
  subscription_expires_at?: string;      // Added
}

interface UserStats {
  healthScore: number;
  longevityScore: number;
  memoryScore: number;
  energyScore: number;
  maxStreak: number;
  totalXP: number;
  league: string;
  totalGames: number;
}

interface StreakData {
  currentStreak: number;
  bestStreak: number;
}

interface DataCacheContextType {
  userProfile: UserProfile | null;
  userStats: UserStats | null;
  streakData: StreakData | null;
  userId: string | null;
  userRank: number | null;
  recentActivities: any[];
  mindBodyStreaks: { yoga: number; mental_maths: number; meditation: number };
  weeklyActivityDays: number;
  ageTrajectoryData: any[];

  loadUserProfile: (force?: boolean) => Promise<void>;
  loadUserStats: (force?: boolean) => Promise<void>;
  loadStreakData: (force?: boolean) => Promise<void>;
  loadRecentActivities: (force?: boolean) => Promise<void>;
  loadMindBodyStreaks: (force?: boolean) => Promise<void>;
  loadAgeTrajectoryData: (force?: boolean) => Promise<void>;

  refreshAll: () => Promise<void>;
  clearCache: () => void;

  isLoadingProfile: boolean;
  isLoadingStats: boolean;
  isLoadingStreaks: boolean;
  isLoadingActivities: boolean;
}

const DataCacheContext = createContext<DataCacheContextType | undefined>(undefined);

// ============================================
// FOOD LOGGING STREAK CALCULATOR
// ============================================
interface StreakResult {
  currentStreak: number;
  bestStreak: number;
}

async function calculateFoodLoggingStreak(userId: string): Promise<StreakResult> {
  try {
    // Get all unique dates where user logged food
    const { data: foodLogs, error } = await supabase
      .from('food_logs')
      .select('date')
      .eq('user_id', userId)
      .order('date', { ascending: false });

    if (error || !foodLogs || foodLogs.length === 0) {
      return { currentStreak: 0, bestStreak: 0 };
    }

    // Get unique dates (in case multiple logs per day)
    const uniqueDatesSet = new Set<string>();
    foodLogs.forEach((log: any) => {
      if (log.date) {
        uniqueDatesSet.add(log.date);
      }
    });

    const uniqueDates = Array.from(uniqueDatesSet);

    // Sort dates in descending order (newest first)
    uniqueDates.sort((a, b) => new Date(b).getTime() - new Date(a).getTime());

    if (uniqueDates.length === 0) {
      return { currentStreak: 0, bestStreak: 0 };
    }

    // Helper function to format date as YYYY-MM-DD
    const formatDate = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    // Get today and yesterday strings
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = formatDate(today);

    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = formatDate(yesterday);

    // ========== CALCULATE CURRENT STREAK ==========
    let currentStreak = 0;
    const mostRecentLog = uniqueDates[0];

    // Streak only counts if most recent log is today or yesterday
    if (mostRecentLog !== todayStr && mostRecentLog !== yesterdayStr) {
      // Last log was before yesterday, streak is broken
      currentStreak = 0;
    } else {
      // Start counting from the most recent log date
      let checkDate = new Date(mostRecentLog);
      checkDate.setHours(0, 0, 0, 0);

      for (const dateStr of uniqueDates) {
        const expectedDateStr = formatDate(checkDate);

        if (dateStr === expectedDateStr) {
          currentStreak++;
          // Move to previous day
          checkDate.setDate(checkDate.getDate() - 1);
        } else if (new Date(dateStr).getTime() < checkDate.getTime()) {
          // Gap found, streak broken
          break;
        }
      }
    }

    // ========== CALCULATE BEST STREAK ==========
    let bestStreak = 0;
    let tempStreak = 1;

    // Sort dates ascending for best streak calculation
    const sortedDatesAsc = [...uniqueDates].sort(
      (a, b) => new Date(a).getTime() - new Date(b).getTime()
    );

    for (let i = 1; i < sortedDatesAsc.length; i++) {
      const prevDate = new Date(sortedDatesAsc[i - 1]);
      const currDate = new Date(sortedDatesAsc[i]);

      // Calculate difference in days
      const diffTime = currDate.getTime() - prevDate.getTime();
      const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 1) {
        // Consecutive day
        tempStreak++;
      } else {
        // Gap found, reset temp streak
        bestStreak = Math.max(bestStreak, tempStreak);
        tempStreak = 1;
      }
    }

    // Don't forget to check the last streak
    bestStreak = Math.max(bestStreak, tempStreak);

    // Best streak should be at least as high as current streak
    bestStreak = Math.max(bestStreak, currentStreak);

    return { currentStreak, bestStreak };
  } catch (error) {
    console.error('Error calculating food logging streak:', error);
    return { currentStreak: 0, bestStreak: 0 };
  }
}

export function DataCacheProvider({ children }: { children: ReactNode }) {
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [userStats, setUserStats] = useState<UserStats | null>(null);
  const [streakData, setStreakData] = useState<StreakData | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [userRank, setUserRank] = useState<number | null>(null);
  const [recentActivities, setRecentActivities] = useState<any[]>([]);
  const [mindBodyStreaks, setMindBodyStreaks] = useState({ yoga: 0, mental_maths: 0, meditation: 0 });
  const [weeklyActivityDays, setWeeklyActivityDays] = useState(0);
  const [ageTrajectoryData, setAgeTrajectoryData] = useState<any[]>([]);

  const [isLoadingProfile, setIsLoadingProfile] = useState(false);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [isLoadingStreaks, setIsLoadingStreaks] = useState(false);
  const [isLoadingActivities, setIsLoadingActivities] = useState(false);

  const [profileCached, setProfileCached] = useState(false);
  const [statsCached, setStatsCached] = useState(false);
  const [streaksCached, setStreaksCached] = useState(false);
  const [activitiesCached, setActivitiesCached] = useState(false);

  const loadUserProfile = useCallback(async (force = false) => {
    if (profileCached && !force && userProfile) return;

    setIsLoadingProfile(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      setUserId(user.id);

      const { data: profile } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (profile) {
        // Check if subscription is still valid
        let isProMember = profile.is_pro_member || false;
        if (isProMember && profile.subscription_expires_at) {
          const expiresAt = new Date(profile.subscription_expires_at);
          isProMember = expiresAt > new Date();
        }

        console.log('[DataCache] Profile loaded - is_pro_member:', isProMember); // Debug log

        setUserProfile({
          name: profile.name,
          email: user.email || '',
          chronological_age: profile.chronological_age,
          biological_age: profile.biological_age,
          gender: profile.gender,
          country: profile.country,
          onboarding_completed: profile.onboarding_completed || false,
          weight_kg: profile.weight_kg,
          height_cm: profile.height_cm,
          is_admin: profile.is_admin || false,
          is_pro_member: isProMember,                              // Added
          subscription_expires_at: profile.subscription_expires_at, // Added
        });
        setProfileCached(true);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setIsLoadingProfile(false);
    }
  }, [profileCached, userProfile]);

  const loadUserStats = useCallback(async (force = false) => {
    if (statsCached && !force && userStats) return;

    setIsLoadingStats(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      await supabase.rpc('update_user_stats', { p_user_id: user.id });

      const { data: healthMetrics } = await supabase
        .from('user_health_metrics')
        .select('total_xp, league, health_score, longevity_score, memory_score, energy_score')
        .eq('user_id', user.id)
        .maybeSingle();

      const { data: streakData } = await supabase.rpc('calculate_overall_completion_streak', {
        p_user_id: user.id
      });

      const { data: gamesCount } = await supabase
        .from('mind_body_activities')
        .select('id', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('activity_type', 'mental_maths');

      const { data: leaderboardPos } = await supabase
        .from('user_leaderboard')
        .select('rank')
        .eq('user_id', user.id)
        .maybeSingle();

      setUserStats({
        healthScore: healthMetrics?.health_score || 0,
        longevityScore: healthMetrics?.longevity_score || 0,
        memoryScore: healthMetrics?.memory_score || 0,
        energyScore: healthMetrics?.energy_score || 0,
        maxStreak: streakData?.current_streak || 0,
        totalXP: healthMetrics?.total_xp || 0,
        league: healthMetrics?.league || 'Bronze',
        totalGames: gamesCount?.count || 0,
      });

      setUserRank(leaderboardPos?.rank || null);
      setStatsCached(true);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setIsLoadingStats(false);
    }
  }, [statsCached, userStats]);

  // ============================================
  // UPDATED: Now uses food logging streak
  // ============================================
  const loadStreakData = useCallback(async (force = false) => {
    if (streaksCached && !force && streakData) return;

    setIsLoadingStreaks(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Calculate streak based on food logging
      const streakResult = await calculateFoodLoggingStreak(user.id);

      setStreakData({
        currentStreak: streakResult.currentStreak,
        bestStreak: streakResult.bestStreak,
      });
      setStreaksCached(true);
    } catch (error) {
      console.error('Error loading streak data:', error);
    } finally {
      setIsLoadingStreaks(false);
    }
  }, [streaksCached, streakData]);

  const loadRecentActivities = useCallback(async (force = false) => {
    if (activitiesCached && !force && recentActivities.length > 0) return;

    setIsLoadingActivities(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: protocols } = await supabase
        .from('daily_protocols')
        .select('*')
        .eq('user_id', user.id)
        .order('date', { ascending: false })
        .limit(7);

      if (protocols) {
        setRecentActivities(protocols);
        setActivitiesCached(true);
      }
    } catch (error) {
      console.error('Error loading recent activities:', error);
    } finally {
      setIsLoadingActivities(false);
    }
  }, [activitiesCached, recentActivities]);

  const loadMindBodyStreaks = useCallback(async (force = false) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Fixed: Now selecting 'completed_at' field which is needed for streak calculation
      const { data: streaks } = await supabase
        .from('mind_body_activities')
        .select('activity_type, completed_at')
        .eq('user_id', user.id)
        .order('completed_at', { ascending: false });

      if (streaks && streaks.length > 0) {
        // Calculate start of current week (Monday)
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const dayOfWeek = today.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() + mondayOffset);
        startOfWeek.setHours(0, 0, 0, 0);

        // Calculate unique days with activities this week
        const weeklyDates = new Set<string>();
        streaks.forEach(activity => {
          const activityDate = new Date(activity.completed_at);
          activityDate.setHours(0, 0, 0, 0);
          if (activityDate >= startOfWeek && activityDate <= today) {
            weeklyDates.add(activityDate.toDateString());
          }
        });
        setWeeklyActivityDays(weeklyDates.size);

        const calculateStreak = (type: string) => {
          const activities = streaks.filter(s => s.activity_type === type);
          if (activities.length === 0) return 0;

          let streak = 0;
          const today = new Date();
          today.setHours(0, 0, 0, 0);

          // Get unique dates for this activity type
          const uniqueDates = new Set<string>();
          activities.forEach(activity => {
            const date = new Date(activity.completed_at);
            date.setHours(0, 0, 0, 0);
            uniqueDates.add(date.toISOString());
          });

          // Convert to sorted array (newest first)
          const sortedDates = Array.from(uniqueDates)
            .map(d => new Date(d))
            .sort((a, b) => b.getTime() - a.getTime());

          // Check if there's activity today or yesterday (streak can continue)
          const mostRecentDate = sortedDates[0];
          const daysSinceMostRecent = Math.floor((today.getTime() - mostRecentDate.getTime()) / (1000 * 60 * 60 * 24));
          
          // If most recent activity is more than 1 day ago, streak is broken
          if (daysSinceMostRecent > 1) return 0;

          // Count consecutive days
          let expectedDate = daysSinceMostRecent === 0 ? today : new Date(today.getTime() - 24 * 60 * 60 * 1000);
          
          for (const date of sortedDates) {
            const diff = Math.floor((expectedDate.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));
            
            if (diff === 0) {
              streak++;
              expectedDate = new Date(expectedDate.getTime() - 24 * 60 * 60 * 1000);
            } else if (diff > 0) {
              // Gap in streak
              break;
            }
          }

          return streak;
        };

        setMindBodyStreaks({
          yoga: calculateStreak('yoga'),
          mental_maths: calculateStreak('mental_maths'),
          meditation: calculateStreak('meditation'),
        });
      } else {
        setMindBodyStreaks({ yoga: 0, mental_maths: 0, meditation: 0 });
        setWeeklyActivityDays(0);
      }
    } catch (error) {
      console.error('Error loading mind body streaks:', error);
    }
  }, []);

  const loadAgeTrajectoryData = useCallback(async (force = false) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: ageData } = await supabase
        .from('age_trajectory')
        .select('*')
        .eq('user_id', user.id)
        .order('date', { ascending: true });

      if (ageData && ageData.length > 0) {
        const chartData = ageData.map((entry) => ({
          date: new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
          biological: entry.biological_age || 0,
          chronological: entry.chronological_age || 0,
        }));
        setAgeTrajectoryData(chartData);
      }
    } catch (error) {
      console.error('Error loading age trajectory:', error);
    }
  }, []);

  const refreshAll = useCallback(async () => {
    await Promise.all([
      loadUserProfile(true),
      loadUserStats(true),
      loadStreakData(true),
      loadRecentActivities(true),
      loadMindBodyStreaks(true),
      loadAgeTrajectoryData(true),
    ]);
  }, [loadUserProfile, loadUserStats, loadStreakData, loadRecentActivities, loadMindBodyStreaks, loadAgeTrajectoryData]);

  const clearCache = useCallback(() => {
    setProfileCached(false);
    setStatsCached(false);
    setStreaksCached(false);
    setActivitiesCached(false);
    setUserProfile(null);
    setUserStats(null);
    setStreakData(null);
    setRecentActivities([]);
    setMindBodyStreaks({ yoga: 0, mental_maths: 0, meditation: 0 });
    setWeeklyActivityDays(0);
    setAgeTrajectoryData([]);
  }, []);

  return (
    <DataCacheContext.Provider
      value={{
        userProfile,
        userStats,
        streakData,
        userId,
        userRank,
        recentActivities,
        mindBodyStreaks,
        weeklyActivityDays,
        ageTrajectoryData,
        loadUserProfile,
        loadUserStats,
        loadStreakData,
        loadRecentActivities,
        loadMindBodyStreaks,
        loadAgeTrajectoryData,
        refreshAll,
        clearCache,
        isLoadingProfile,
        isLoadingStats,
        isLoadingStreaks,
        isLoadingActivities,
      }}
    >
      {children}
    </DataCacheContext.Provider>
  );
}

export function useDataCache() {
  const context = useContext(DataCacheContext);
  if (context === undefined) {
    throw new Error('useDataCache must be used within a DataCacheProvider');
  }
  return context;
}